import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { useTasksTab } from "../../hooks/Tasks/useTasksTab";
import { WORKSTREAM_ORDER, PRIMARY_COLOR, CARD_SX } from "./constants";
import { WorkstreamName, Task } from "./types";
import { TaskListSkeleton } from "./Skeletons";
import { tokens } from "../../theme/tokens";

function getTaskRowInfo(task: Task) {
  const total = task.targets.length;
  const done = task.targets.filter(
    (t) => t.status === "WORKED" || t.status === "BAD",
  ).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const color = done === 0 ? tokens.color.border.strong : pct === 100 ? tokens.color.workflow.worked.fg : tokens.color.workflow.doing.fg;
  return { done, total, pct, color };
}

const TaskListPanel: React.FC = () => {
  const { filteredTasks, selectedTaskId, setSelectedTaskId, isLoading, activePoiId } =
    useTasksTab();

  const grouped = useMemo(
    () =>
      WORKSTREAM_ORDER.reduce<Record<WorkstreamName, Task[]>>(
        (acc, ws) => {
          const wsTasks = filteredTasks.filter((t) => t.group_label === ws);
          if (wsTasks.length > 0) acc[ws] = wsTasks;
          return acc;
        },
        {} as Record<WorkstreamName, Task[]>,
      ),
    [filteredTasks],
  );

  return (
    <Box
      sx={{
        ...CARD_SX,
        width: "255px",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, overflowY: "auto", py: "10px" }}>
        {isLoading ? (
          <TaskListSkeleton />
        ) : Object.entries(grouped).length === 0 ? (
          <Box sx={{ px: "16px", py: "20px", textAlign: "center" }}>
            <span style={{ fontSize: "12px", color: tokens.color.border.strong }}>
              {activePoiId === null ? "No tasks yet." : "No tasks for this POI."}
            </span>
          </Box>
        ) : (
          Object.entries(grouped).map(([ws, tasks]) => (
            <Box key={ws} sx={{ mb: "6px" }}>
              <Box sx={{ px: "14px", pt: "8px", pb: "4px" }}>
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 700,
                    color: tokens.color.text.secondary,
                    textTransform: "uppercase",
                    letterSpacing: "0.07em",
                  }}
                >
                  {ws}
                </span>
              </Box>

              {tasks.map((task) => {
                const isSelected = selectedTaskId === task.task_id;
                const { done, total, pct, color } = getTaskRowInfo(task);
                const hasWinner = task.primary_target_ids.length > 0;

                return (
                  <Box
                    key={task.task_id}
                    onClick={() => setSelectedTaskId(isSelected ? null : task.task_id)}
                    sx={{
                      px: "14px",
                      py: "7px",
                      cursor: "pointer",
                      backgroundColor: isSelected ? tokens.color.surface.hoverStrong : "transparent",
                      borderLeft: `3px solid ${isSelected ? PRIMARY_COLOR : "transparent"}`,
                      transition: "all 0.12s ease",
                      "&:hover": {
                        backgroundColor: isSelected ? tokens.color.surface.hoverStrong : tokens.color.surface.sunken,
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: color,
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontSize: "12px",
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected ? tokens.color.text.primary : tokens.color.text.primary,
                          flex: 1,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {task.title}
                      </span>
                      {hasWinner && (
                        <span style={{ fontSize: "11px", color: tokens.color.workflow.doing.fg, flexShrink: 0 }}>
                          ★
                        </span>
                      )}
                      <span
                        style={{
                          fontSize: "11px",
                          color: tokens.color.text.secondary,
                          flexShrink: 0,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {done}/{total}
                      </span>
                    </Box>

                    {total > 0 && (
                      <Box sx={{ mt: "5px", pl: "16px" }}>
                        <Box
                          sx={{
                            height: "2px",
                            background: tokens.color.surface.subtle,
                            borderRadius: 99,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${pct}%`,
                              background: color,
                              borderRadius: 99,
                              transition: "width 0.3s ease",
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default TaskListPanel;
